import type { Column, RowData } from '@tanstack/react-table';
import { JSX } from 'react';
import { useIntl } from 'react-intl';

import { HideIcon, ViewIcon } from '@dynatrace/strato-icons';

import { Button } from '../../../../../buttons/button/Button.js';
import { Tooltip } from '../../../../../overlays/tooltip/Tooltip.js';
import type { TDataTableFeatures } from '../../../features/types/features.types.js';
import { dataTableMessages } from '../../../messages.js';

interface ColumnVisibilityToggleProps<TData extends RowData> {
  /**
   * The column to render the visibility toggle for.
   */
  column: Column<TDataTableFeatures, TData>;
  /**
   * The display label of the column, included in the aria-label for screen readers.
   */
  columnLabel: string;
  /**
   * Whether visibility can be changed for this column.
   */
  enableToggle: boolean;
  /**
   * The current visibility state of the column.
   */
  currentState: boolean | 'indeterminate';
  /**
   * Callback when the toggle is clicked and visibility of the column changes.
   */
  onVisibilityChange: (
    column: Column<TDataTableFeatures, TData>,
    currentVisibility: boolean | 'indeterminate',
  ) => void;
}

/**
 * Icon toggle for showing/hiding a column in the DataTable column settings.
 * @internal
 */
export function ColumnVisibilityToggle<TData extends RowData>(
  props: Readonly<ColumnVisibilityToggleProps<TData>>,
): JSX.Element {
  const {
    column,
    columnLabel,
    enableToggle,
    currentState,
    onVisibilityChange,
  } = props;
  const intl = useIntl();

  // Fully hidden = the column (or every hideable child of a parent) is hidden,
  // not the 'indeterminate' mixed state. Only then is the eye icon closed.
  const isFullyHidden = currentState === false;
  // Clicking shows the column unless it is fully visible; an indeterminate
  // parent reveals its hidden children, so that also counts as "show".
  const willShowOnClick = currentState !== true;

  const tooltipText = enableToggle
    ? intl.formatMessage(dataTableMessages.toggleColumnVisibilityLabel, {
        isHidden: willShowOnClick,
      })
    : intl.formatMessage(dataTableMessages.columnVisibilityDisabledLabel, {
        isHidden: isFullyHidden,
      });
  const ariaLabel = enableToggle
    ? intl.formatMessage(dataTableMessages.toggleColumnVisibilityAriaLabel, {
        isHidden: willShowOnClick,
        columnName: columnLabel,
      })
    : intl.formatMessage(dataTableMessages.columnVisibilityDisabledAriaLabel, {
        isHidden: isFullyHidden,
        columnName: columnLabel,
      });

  const dataDtProperties = `state:${willShowOnClick ? 'show' : 'hide'}`;

  return (
    <Tooltip text={tooltipText} placement="right">
      <Button
        size="condensed"
        variant="default"
        aria-label={ariaLabel}
        aria-disabled={!enableToggle}
        onClick={() => {
          if (enableToggle) {
            onVisibilityChange(column, currentState);
          }
        }}
        data-dt-children-name="ColumnSettingsShowHideColumn"
        data-dt-properties={dataDtProperties}
      >
        <Button.Prefix>
          {isFullyHidden ? <HideIcon /> : <ViewIcon />}
        </Button.Prefix>
      </Button>
    </Tooltip>
  );
}
